import { createContext, useContext, useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useFlag } from "@unleash/proxy-client-react";

import { CfLoader } from "@/shared/components/common/CfLoader/CfLoader";
import { logger } from "@/shared/services/SentryLogger.service";

import {
  ALL_FARM_MODULES_ENABLED,
  type FarmModule,
  type FarmModuleSettings,
} from "./farmModules.constants";

import { farmModulesQuery } from "./farmModules.api";

const FARM_MODULES_FLAG = "dev-2163-farm-modules";

type FarmModules = {
  settings: FarmModuleSettings;
  isEnabled: (module: FarmModule) => boolean;
  isDisabled: (module: FarmModule) => boolean;
};

const FarmModulesContext = createContext<FarmModuleSettings>(
  ALL_FARM_MODULES_ENABLED,
);

type FarmModulesProviderProps = {
  farmId: string;
  children: React.ReactNode;
};

const FarmModulesProvider = ({
  children,
  farmId,
}: FarmModulesProviderProps): React.ReactElement => {
  const isModularityEnabled = useFlag(FARM_MODULES_FLAG);
  const { data, error, isPending } = useQuery({
    ...farmModulesQuery(farmId),
    enabled: isModularityEnabled,
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    logger.warn("Farm modules unavailable, showing all navigation", { farmId });
  }, [error, farmId]);

  if (isModularityEnabled && isPending) {
    return <CfLoader />;
  }

  return (
    <FarmModulesContext.Provider value={data ?? ALL_FARM_MODULES_ENABLED}>
      {children}
    </FarmModulesContext.Provider>
  );
};

const useFarmModules = (): FarmModules => {
  const settings = useContext(FarmModulesContext);

  return useMemo(
    () => ({
      settings,
      isEnabled: (module: FarmModule) => settings[module],
      isDisabled: (module: FarmModule) => !settings[module],
    }),
    [settings],
  );
};

export { FarmModulesProvider, useFarmModules };
export type { FarmModules };
